import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import useBulkAddClients from '@/hooks/useBulkAddClients';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const EMPTY = {
  firstName: '',
  lastName: '',
  emailAddress: '',
  emailAddress2: '',
  coOwnerFirstName: '',
  coOwnerLastName: '',
  phoneNumber: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
};

const AddClientModal = ({ isOpen, onClose }: Props) => {
  const [form, setForm] = useState(EMPTY);
  const { mutate: addClient, isPending } = useBulkAddClients();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addClient([form], {
      onSuccess: () => {
        toast.success('Client added successfully');
        setForm(EMPTY);
        onClose();
      },
      onError: () => {
        toast.error('Failed to add client');
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add Client
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                placeholder="First name"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                placeholder="Last name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emailAddress">Email *</Label>
              <Input
                id="emailAddress"
                name="emailAddress"
                type="email"
                value={form.emailAddress}
                onChange={handleChange}
                placeholder="owner@example.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emailAddress2">Co-Owner Email</Label>
              <Input
                id="emailAddress2"
                name="emailAddress2"
                type="email"
                value={form.emailAddress2}
                onChange={handleChange}
                placeholder="co-owner@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="coOwnerFirstName">Co-Owner First Name</Label>
              <Input
                id="coOwnerFirstName"
                name="coOwnerFirstName"
                value={form.coOwnerFirstName}
                onChange={handleChange}
                placeholder="Co-owner first name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="coOwnerLastName">Co-Owner Last Name</Label>
              <Input
                id="coOwnerLastName"
                name="coOwnerLastName"
                value={form.coOwnerLastName}
                onChange={handleChange}
                placeholder="Co-owner last name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Phone</Label>
              <Input
                id="phoneNumber"
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={handleChange}
                placeholder="(555) 000-0000"
              />
            </div>
          </div>

          <div className="border-t pt-4 space-y-4">
            <p className="text-sm font-medium text-muted-foreground">Property Address</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="addressLine1">Street Address</Label>
                <Input
                  id="addressLine1"
                  name="addressLine1"
                  value={form.addressLine1}
                  onChange={handleChange}
                  placeholder="123 Main St"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="addressLine2">Address Line 2</Label>
                <Input
                  id="addressLine2"
                  name="addressLine2"
                  value={form.addressLine2}
                  onChange={handleChange}
                  placeholder="Apt, Suite, Unit (optional)"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  placeholder="City"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    name="state"
                    value={form.state}
                    onChange={handleChange}
                    placeholder="TX"
                    maxLength={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zipCode">ZIP</Label>
                  <Input
                    id="zipCode"
                    name="zipCode"
                    value={form.zipCode}
                    onChange={handleChange}
                    placeholder="77001"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Adding...' : 'Add Client'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddClientModal;
